import { ref, type ComputedRef, type Ref } from 'vue';
import { downloadBlobFile, handleApiError, useCustomToast } from '@/shared/lib';
import { reminderHistoryApi, type NoticeListQuery } from '@/entities/ReminderHistory';
import type { ReminderGeneralHistoryFilters, ReminderHistoryFilters, ReminderTab, SortOption } from './types';

interface UseReminderHistoryExportParams {
  activeTab: Ref<ReminderTab>;
  searchTerm: Ref<string>;
  selectedSort: ComputedRef<SortOption>;
  currentFilters: Ref<ReminderHistoryFilters>;
}

function getRecipientId(
  activeTab: ReminderTab,
  filters: ReminderHistoryFilters
): number | undefined {
  if (activeTab !== 'general-history') {
    return undefined;
  }

  return (filters as ReminderGeneralHistoryFilters).recipientId;
}

export function useReminderHistoryExport(params: UseReminderHistoryExportParams) {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const buildExportQuery = (): NoticeListQuery => {
    const filters = params.currentFilters.value;
    const trimmedSearchTerm = params.searchTerm.value.trim();

    return {
      term: trimmedSearchTerm.length > 0 ? trimmedSearchTerm : undefined,
      sort: params.selectedSort.value.sort,
      order: params.selectedSort.value.order,
      createdAt: filters.createdAt,
      createdBy: filters.createdBy,
      category: filters.category,
      recipientId: getRecipientId(params.activeTab.value, filters),
    };
  };

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Exportação em andamento. Por favor, aguarde.',
        type: 'warning',
      });

      const query = buildExportQuery();
      const { blob, exportedCount, filename } =
        params.activeTab.value === 'my-history'
          ? await reminderHistoryApi.exportMyHistoryExcel(query)
          : await reminderHistoryApi.exportGeneralHistoryExcel(query);

      downloadBlobFile(blob, filename);

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: exportedCount === 1 ? '1 aviso exportado' : `${exportedCount} avisos exportados`,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(
        error,
        'Não foi possível exportar os avisos. Tente novamente.'
      );

      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    isExporting,
    handleExportToExcel,
  };
}
